/**
 * Tracked keywords panel for Weekly Review — collapsed by default (secondary control).
 */

import React, { useState } from 'react';
import { Badge, Button, Input, LoadingSpinner } from '@tracekit/core';
import {
  CheckSquare,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Save,
  Search,
  Square,
  Tag,
} from 'lucide-react';
import { normalizeMonitorKeyword } from '../../../utils/alertKeywordRecommendations';
import type { WeeklyReviewKeywordCatalogEntry } from '../../../utils/weeklyReviewTrackedKeywords';

export interface WeeklyReviewTrackedKeywordsPanelProps {
  catalog: WeeklyReviewKeywordCatalogEntry[];
  filteredCatalog: WeeklyReviewKeywordCatalogEntry[];
  draftSelectionSet: Set<string>;
  draftSelectedCount: number;
  catalogCount: number;
  trackedKeywordCount: number;
  filterText: string;
  customInput: string;
  isDirty: boolean;
  isLoadingCatalog?: boolean;
  catalogError?: unknown;
  onFilterTextChange: (value: string) => void;
  onCustomInputChange: (value: string) => void;
  onToggleKeyword: (keyword: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onAddCustomKeyword: () => void;
  onSave: () => void;
  onResetDraft: () => void;
  /** When true, expand the editor on first paint (tests / deep links). Default collapsed. */
  defaultExpanded?: boolean;
}

export function WeeklyReviewTrackedKeywordsPanel({
  catalog,
  filteredCatalog,
  draftSelectionSet,
  draftSelectedCount,
  catalogCount,
  trackedKeywordCount,
  filterText,
  customInput,
  isDirty,
  isLoadingCatalog,
  catalogError,
  onFilterTextChange,
  onCustomInputChange,
  onToggleKeyword,
  onSelectAll,
  onClearAll,
  onAddCustomKeyword,
  onSave,
  onResetDraft,
  defaultExpanded = false,
}: WeeklyReviewTrackedKeywordsPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleCustomSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onAddCustomKeyword();
  };

  return (
    <section
      className="rounded-lg border border-theme-border/80 bg-theme-surface/30"
      aria-label="Tracked keywords"
    >
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-theme-surface/60 transition-colors rounded-lg"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2 min-w-0">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-theme-text-secondary flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-theme-text-secondary flex-shrink-0" />
          )}
          <Tag className="h-3.5 w-3.5 text-theme-text-secondary flex-shrink-0" />
          <span className="text-xs font-medium text-theme-text-secondary">
            Tracked keywords
          </span>
          <Badge variant="default" className="text-[10px]">
            {trackedKeywordCount} active
          </Badge>
          {isDirty ? (
            <Badge variant="default" className="text-[10px] bg-amber-50 text-amber-800">
              Unsaved
            </Badge>
          ) : null}
        </span>
        <span className="text-[11px] text-theme-text-secondary flex-shrink-0">
          {expanded ? 'Hide' : 'Edit'}
        </span>
      </button>

      {expanded ? (
        <div className="border-t border-theme-border/60 px-3 pb-3 pt-2 space-y-3">
          <p className="text-xs text-theme-text-secondary">
            Choose which suggested keywords drive this review. Changes apply after Save.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onSelectAll}>
              <CheckSquare className="h-4 w-4 mr-1.5" />
              Select all
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onClearAll}>
              <Square className="h-4 w-4 mr-1.5" />
              Clear
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onResetDraft} disabled={!isDirty}>
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Reset
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={onSave} disabled={!isDirty}>
              <Save className="h-4 w-4 mr-1.5" />
              Save
            </Button>
          </div>

          <form onSubmit={handleCustomSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1">
              <label
                htmlFor="weekly-review-custom-keyword"
                className="text-xs font-medium text-theme-text-secondary"
              >
                Add custom keyword
              </label>
              <Input
                id="weekly-review-custom-keyword"
                value={customInput}
                onChange={(event) => onCustomInputChange(event.target.value)}
                placeholder="e.g. digital twin, watershed modeling"
              />
            </div>
            <Button type="submit" variant="outline" size="sm" disabled={!customInput.trim()}>
              Add
            </Button>
          </form>

          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-theme-text-secondary" />
              <Input
                value={filterText}
                onChange={(event) => onFilterTextChange(event.target.value)}
                placeholder="Filter recommended keywords"
                className="pl-9"
                aria-label="Filter recommended keywords"
              />
            </div>
            <p className="text-xs text-theme-text-secondary">
              {draftSelectedCount} of {catalogCount} selected
              {filterText.trim() ? ` · showing ${filteredCatalog.length} matches` : ''}
            </p>
          </div>

          {catalogError ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              Could not load the full recommended keyword catalog.
            </p>
          ) : isLoadingCatalog && catalog.length === 0 ? (
            <div className="flex items-center gap-2 py-4 justify-center text-sm text-theme-text-secondary">
              <LoadingSpinner />
              Loading recommended keywords…
            </div>
          ) : catalog.length === 0 ? (
            <p className="text-sm text-theme-text-secondary">
              No recommended keywords yet. Generate suggested keywords on the Opportunities tab first.
            </p>
          ) : filteredCatalog.length === 0 ? (
            <p className="text-sm text-theme-text-secondary">No keywords match your filter.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto rounded-md border border-theme-border/80 bg-white/40 dark:bg-slate-900/30 p-2">
              <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                {filteredCatalog.map((entry) => {
                  const isSelected = draftSelectionSet.has(normalizeMonitorKeyword(entry.keyword));
                  return (
                    <li key={entry.keyword}>
                      <label className="flex items-start gap-2 rounded-md px-1.5 py-1 hover:bg-white/70 dark:hover:bg-slate-800/70 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleKeyword(entry.keyword)}
                          className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="min-w-0">
                          <span className="block text-xs text-gray-900 dark:text-gray-100 break-words">
                            {entry.keyword}
                          </span>
                          <span className="flex flex-wrap items-center gap-1 mt-0.5">
                            {entry.isCustom ? (
                              <Badge variant="default" className="text-[10px]">
                                Custom
                              </Badge>
                            ) : null}
                            {entry.confidencePct != null && entry.confidencePct > 0 ? (
                              <span className="text-[10px] text-theme-text-secondary">
                                Suggested {Math.round(entry.confidencePct)}%
                              </span>
                            ) : null}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
